import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { BenefitDto, CoverageDto } from 'theSource/queries';
import { PlanInfo, DeclineInfo, DependentList } from './components';
import { DependentDto } from 'theSource/features/FamilyMembers/types';
import { SubscriberResponseDto } from 'theSource/types';
import {
  CoverageButtonStyled,
  ElectionWrapperStyled,
} from './CoverageCardStyled';
import { AlertBox } from 'components';
import { getPremiumStyled, RETURN_VALUE_ONLY } from 'enrollment/utils';

interface CoverageCardProps {
  coverage?: CoverageDto;
  benefit?: BenefitDto;
  subscriberProfile?: SubscriberResponseDto;
  dependents?: DependentDto[];
}

export const CoverageCard = ({
  coverage,
  benefit,
  subscriberProfile,
  dependents,
}: CoverageCardProps) => {
  const { t } = useTranslation('pages.benefits');
  const benefitLookup = benefit?.benefit_lookup_code || '';

  const navigate = useNavigate();
  const handleNavigate = () => {
    navigate(benefitLookup, { state: { benefit: benefit } });
  };

  const hasDependentCoverages = coverage?.dependent_coverages
    ? coverage?.dependent_coverages.length > 0
    : false;
  const isScreenSmall = window.innerWidth < 744;
  const selectedDependentIds = coverage?.dependent_coverages
    .filter((depCov) => {
      if (
        !depCov.termination_date ||
        new Date(depCov.termination_date) > new Date()
      ) {
        return true;
      }
      return false;
    })
    ?.map((dependent) => dependent.dependent_id);
  const selectedDependents = dependents?.filter((dependent) =>
    selectedDependentIds?.includes(dependent.id)
  );

  const isDecline = coverage?.req_coverage_level_lookup_code === 'decline';
  const isWaitingForEoi = coverage?.waiting_for_eoi || false;
  const isWaitingForDocRequest = coverage?.waiting_for_doc_requests || false;
  const isCoveragePending = isWaitingForEoi || isWaitingForDocRequest;
  const isVolumeBenefit = benefit?.benefit_class === 'volume';

  const volume = Number(coverage?.volume) || 0;
  const reqVolume = Number(coverage?.req_volume) || 0;
  const actualAmount = Number(coverage?.volume) || 0;
  const reqPremium = Number(coverage?.req_subscriber_premium) || 0;
  const actualPremium = Number(coverage?.subscriber_premium) || 0;

  const reqAmount = getPremiumStyled({
    premium: String(reqVolume),
    valueOnly: RETURN_VALUE_ONLY,
  });
  const differenceAmount = getPremiumStyled({
    premium: String(reqVolume - actualAmount),
    valueOnly: RETURN_VALUE_ONLY,
  });
  const differencePremium = getPremiumStyled({
    premium: String(reqPremium - actualPremium),
    valueOnly: RETURN_VALUE_ONLY,
  });

  let alertText = '';
  if (isWaitingForEoi) {
    alertText = t('alertEoi', {
      reqAmount: reqAmount,
      differenceAmount: differenceAmount,
      differencePremium: differencePremium,
    });
  } else if (isWaitingForDocRequest) {
    alertText = t('alertDocRequest', { differencePremium: differencePremium });
  }

  return (
    <CoverageButtonStyled
      onClick={handleNavigate}
      id={`${benefitLookup}-${coverage?.coverage_id}-coverage-card`}
      data-testid={`${benefitLookup}-${coverage?.coverage_id}-coverage-card`}
    >
      <ElectionWrapperStyled>
        <div className="d-flex">
          {!isDecline && (
            <PlanInfo
              planName={coverage?.req_org_plan_name || ''}
              carrierName={coverage?.req_carrier_name || ''}
              subscriberPremium={coverage?.subscriber_premium || 0}
              volume={volume}
              isVolumeBenefit={isVolumeBenefit}
              coverage={coverage}
              benefitDetails={benefit}
            />
          )}
          {isDecline && (
            <DeclineInfo benefitName={benefit?.org_benefit_name || ''} />
          )}
          <div className="ml-2 ml-lg-5">
            <ChevronRightIcon style={{ color: '#000' }} />
          </div>
        </div>
        {!isDecline && hasDependentCoverages && !isScreenSmall && (
          <DependentList
            dependents={selectedDependents}
            subscriberProfile={subscriberProfile}
          />
        )}

        {isCoveragePending && (
          <div className="mt-3">
            <AlertBox innerText={alertText} />
          </div>
        )}
      </ElectionWrapperStyled>
    </CoverageButtonStyled>
  );
};
